import apiFetch from '@scale/frontend-lib/api-fetch';
import { IGraderConfig, IScenarioGraderParams } from '@scale/shared/grader/types';
import {
  AutoReviewerConfigDTO,
  IAutoReviewerVariable,
  RunAutoReviewerResult,
  ILLMCheckConfig,
} from '@scale/shared/autoReviewer';
import {
  ClaimCalibrationAuditTaskResponse,
  CreateTasksRequest,
  CreateTasksResponse,
  FetchProjectNotificationDetailRequest,
  FetchProjectNotificationDetailResponse,
  GetInstructionsResponse,
  GetProjectResponse,
  UpdateInstructionsRequest,
  GenerateBenchmarkRequest,
  GenerateBenchmarkResponse,
  BenchmarkConfigs,
  CalculateBackfillRequest,
  CalculateBackfillResponse,
  AvailableCorruptibleFieldsResponse,
  RunBackfillRequest,
  RunBackfillResponse,
  ListCorruptionTemplatesResponse,
  UpsertCorruptionTemplateRequest,
  UpsertCorruptionTemplateResponse,
  GetCorruptionTemplatesResponse,
  ArchiveCorruptionTemplateRequest,
  ArchiveCorruptionTemplateResponse,
  TestRunCorruptionTemplateRequest,
  TestRunCorruptionTemplateResponse,
  BackfillAutoReviewerRequest,
  BackfillAutoReviewerResponse,
  ValidateTaskUploadRequest,
  ValidateTaskUploadResponse,
  GetDelayedBackfillsRequest,
  GetDelayedBackfillsResponse,
} from '@scale/shared/mist/types';
import {
  QMUpdateBenchmarkRequest,
  QMUpdateBenchmarkResponse,
} from '@scale/shared/qm/qmRequestTypes';
import { BenchmarkBulkRequest, IBenchmarkBulkLookupJson } from '@scale/shared/types/project';
import { Promise } from 'bluebird';
import { WorkerReviewLevel, ReviewLevel } from '@scale/shared/defaults';
import { GetTemplateResponse, GetTemplateParams } from '@scale/shared/taskTemplate/requestTypes';
import {
  FetchFeedbackForRunResponseDTO,
  ModelQualitativeFeedbackSurveyDTO,
} from '@scale/shared/modelEval';

const EMPTY_RESPONSE = {};

class CommunicationService {
  getProject = async (projectId: string) => {
    const response = await apiFetch<GetProjectResponse>(
      null,
      `internal/mist-internal/project/${projectId}`,
      {
        method: 'GET',
      },
    );
    return response;
  };

  getInstructions = async (projectId: string) => {
    const response = await apiFetch<GetInstructionsResponse>(
      null,
      `internal/mist-internal/project/${projectId}/instructions`,
      {
        method: 'GET',
      },
    );
    return response;
  };

  updateInstructions = async (projectId: string, instructions: string) => {
    const response = await apiFetch<UpdateInstructionsRequest>(
      null,
      `internal/mist-internal/project/${projectId}/instructions`,
      {
        method: 'POST',
        body: instructions,
      },
    );
    return response;
  };

  getCalibrationTaskParams = async (taskId: string) => {
    const response = await apiFetch<any>(
      null,
      `internal/mist-internal/task/${taskId}/fetchCalibrationTaskParams`,
      {
        method: 'GET',
      },
    );
    return response;
  };

  // Claim specific calibration task for audit
  claimCalibrationAuditTask = async (taskId: string) => {
    const response = await apiFetch<ClaimCalibrationAuditTaskResponse>(
      null,
      `internal/mist-internal/task/${taskId}/claimCalibrationAuditTask`,
      {
        method: 'POST',
      },
    );
    return response;
  };

  getTaskParamsAndResponse = async (taskOrScenario: string) => {
    const response = await apiFetch<any>(
      null,
      `internal/mist-internal/task/${taskOrScenario}/fetchTaskParamsAndResponse`,
    );
    return response;
  };

  updateBenchmarkConfig = async ({
    trainingScenarioId,
    updatedConfig,
  }: {
    trainingScenarioId: string;
    updatedConfig: QMUpdateBenchmarkRequest;
  }) => {
    const response = await apiFetch<QMUpdateBenchmarkResponse, QMUpdateBenchmarkRequest>(
      null,
      `corp-api/qm/benchmark/${trainingScenarioId}`,
      {
        method: 'PUT',
        body: updatedConfig,
      },
    );

    return response;
  };

  editBenchmark = async ({ bmId, params }: { bmId: string; params: QMUpdateBenchmarkRequest }) => {
    const response = await apiFetch<QMUpdateBenchmarkResponse>(
      null,
      `corp-api/qm/benchmark/${bmId}`,
      {
        method: 'PUT',
        body: params,
      },
    );

    return response;
  };

  createBenchmark = async ({
    projectId,
    taskId,
    configs,
    initialResponse,
    expectedResponse,
    sceneConfigs,
    feedbackMessage,
    instructionItemIds,
    metadata,
    params,
  }: {
    projectId: string;
    taskId: string;
    configs: BenchmarkConfigs;
    initialResponse: any;
    expectedResponse: any;
    sceneConfigs?: IGraderConfig[];
    feedbackMessage?: string;
    instructionItemIds?: string[];
    metadata?: any;
    params?: any;
  }) => {
    const graderParams: IScenarioGraderParams | undefined = sceneConfigs
      ? {
          sceneConfigs,
        }
      : undefined;
    const body: BenchmarkBulkRequest = {
      objectIds: [taskId],
      options: {
        isEnabledBenchmark: true,
        isAttempterBenchmark: configs.isAttempter,
        isInstructiveBenchmark: configs.isInstructive,
        isWallBenchmark: configs.inWall,
        initialResponse: configs.isAttempter ? EMPTY_RESPONSE : initialResponse,
        expectedResponse,
        graderParams,
        feedbackMessage,
        instructionItems: instructionItemIds,
        reviewLevels: configs.isAttempter
          ? []
          : [WorkerReviewLevel.Normal, WorkerReviewLevel.Level1, WorkerReviewLevel.Corp],
        metadata,
        params,
      },
    };
    const response = await apiFetch<IBenchmarkBulkLookupJson>(
      null,
      `corp-api/projects/${projectId}/benchmarks/bulk`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      },
    );
    return response;
  };

  fetchAvailableAttemptCorruptibleFields = async () => {
    const response = await apiFetch<AvailableCorruptibleFieldsResponse>(
      null,
      `internal/mist-internal/benchmark/fetchAvailableAttemptCorruptibleFields`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    return response;
  };

  // Mocked
  createMockTasks = async (params: CreateTasksRequest) => {
    const response = await apiFetch<CreateTasksResponse>(
      null,
      'internal/mist-internal/task/create-tasks',
      { method: 'POST', body: params },
    );
    return response;
  };

  getSyntheticBenchmarkDataFromStrategy = async (params: GenerateBenchmarkRequest) => {
    const response = await apiFetch<GenerateBenchmarkResponse>(
      null,
      `internal/mist-internal/benchmark/generateBenchmarkFromStrategy`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      },
    );

    return response;
  };

  updateBenchmarksStatus = async (options: {
    benchmarkIds: string[];
    body: QMUpdateBenchmarkRequest;
  }) => {
    const { benchmarkIds, body } = options;

    const response = await Promise.map(
      benchmarkIds,
      async benchmarkId => {
        await apiFetch<QMUpdateBenchmarkResponse, QMUpdateBenchmarkRequest>(
          null,
          `corp-api/qm/benchmark/${benchmarkId}`,
          {
            method: 'PUT',
            body,
          },
        );
      },
      { concurrency: 10 },
    );
    return response;
  };

  getNotificationDetail = async (notificationId: string) => {
    const response = await apiFetch<
      FetchProjectNotificationDetailResponse,
      FetchProjectNotificationDetailRequest
    >(null, `internal/mist-internal/notification/fetchProjectNotificationDetail`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: notificationId }),
    });
    return response;
  };

  getTaskTemplate = async (params: GetTemplateParams) => {
    const response = await apiFetch<GetTemplateResponse>(
      null,
      `internal/task-template/${params.project}`,
      {
        method: 'GET',
      },
    );
    return response;
  };

  createTasks = async (params: CreateTasksRequest) => {
    // TODO - actually create tasks here once backend is ready
    const response = await apiFetch<CreateTasksResponse>(
      null,
      `internal/mist-internal/task/create-tasks`,
      {
        method: 'POST',
        body: params,
      },
    );
    return response;
  };

  calculateBackfillTasks = async (params: CalculateBackfillRequest) => {
    const response = apiFetch<CalculateBackfillResponse>(
      null,
      `internal/mist-internal/task/calculateBackfillTasks`,
      {
        method: 'POST',
        body: params,
      },
    );
    return response;
  };

  runBackfillTasks = async (params: RunBackfillRequest) => {
    const response = apiFetch<RunBackfillResponse>(
      null,
      `internal/mist-internal/task/runBackfillTasks`,
      {
        method: 'POST',
        body: params,
      },
    );
    return response;
  };

  getDelayedBackfills = async (params: GetDelayedBackfillsRequest) => {
    const response = await apiFetch<GetDelayedBackfillsResponse>(
      null,
      `internal/mist-internal/task/delayedBackfills`,
      {
        method: 'GET',
        query: params,
      },
    );
    return response;
  };

  getTestTaskAttemptData = async (taskAttemptId: string) => {
    const response = await apiFetch<{
      variables: IAutoReviewerVariable[];
      maxTurns: number;
      numTurns: number;
    }>(null, `internal/mist-internal/autoReviewer/testAttemptData`, {
      method: 'GET',
      query: { taskAttemptId },
    });
    return response;
  };

  getAutoReviewerConfig = async (projectId: string) => {
    const response = await apiFetch<AutoReviewerConfigDTO>(
      null,
      `internal/mist-internal/autoReviewer/config`,
      {
        method: 'GET',
        query: { projectId },
      },
    );
    return response;
  };

  getAutoReviewerCostData = async (projectId: string) => {
    const response = await apiFetch<number>(null, `internal/mist-internal/autoReviewer/cost`, {
      method: 'GET',
      query: { projectId },
    });
    return response;
  };

  saveAutoReviewerConfig = async (autoReviewerConfig: Partial<AutoReviewerConfigDTO>) => {
    const response = await apiFetch(null, `internal/mist-internal/autoReviewer/config`, {
      method: 'POST',
      body: { autoReviewerConfig },
    });
    return response;
  };

  testLLMCheckRun = async (params: { taskAttemptId: string; llmCheckConfig: ILLMCheckConfig }) => {
    const response = await apiFetch<RunAutoReviewerResult>(
      null,
      `internal/mist-internal/autoReviewer/testRunLLMCheck`,
      {
        method: 'POST',
        body: params,
      },
    );
    return response;
  };

  runAutoReviewer = async ({ taskAttemptId }: { taskAttemptId: string }) => {
    const response = await apiFetch<{
      [taskAttemptId: string]: {
        result: RunAutoReviewerResult;
      };
    }>(null, `internal/mist-internal/autoReviewer/bulkRunAutoReviewer`, {
      method: 'POST',
      body: {
        taskAttemptIds: [taskAttemptId],
      },
    });
    return response[taskAttemptId].result;
  };

  runBackfillAutoReviewer = async (params: BackfillAutoReviewerRequest) => {
    const response = await apiFetch<BackfillAutoReviewerResponse>(
      null,
      `internal/mist-internal/autoReviewer/backfillAutoReviewer`,
      {
        method: 'POST',
        body: params,
      },
    );
    return response;
  };

  fetchRecentTaskAttempts = async (projectId: string, numTaskAttempts: number) => {
    const response = await apiFetch<{
      taskAttempts: {
        id: string;
        reviewLevel: ReviewLevel;
      }[];
    }>(null, `internal/mist-internal/autoReviewer/fetchRecentTaskAttempts`, {
      method: 'POST',
      body: { projectId, numTaskAttempts },
    });
    return response;
  };

  bulkRunAutoReviewer = async (params: { taskAttemptIds: string[] }) => {
    const response = await apiFetch<{
      [taskAttemptId: string]: {
        workerId: string;
        reviewLevel: ReviewLevel;
        result: RunAutoReviewerResult;
      };
    }>(null, `internal/mist-internal/autoReviewer/bulkRunAutoReviewer`, {
      method: 'POST',
      body: params,
    });
    return response;
  };

  validateBatchesForRun = async (params: { projectId: string; batchIds: string[] }) => {
    const response = await apiFetch<{ valid: boolean; errorMsg: string }>(
      null,
      `corp-api/rapid/validateBatchIdsForRun`,
      {
        method: 'POST',
        body: { ...params, onCreation: true },
      },
    );
    return response;
  };

  upsertCorruptionTemplate = async (params: UpsertCorruptionTemplateRequest) => {
    const response = await apiFetch<UpsertCorruptionTemplateResponse>(
      null,
      'internal/mist-internal/benchmark/corruptions/upsertCorruptionTemplate',
      {
        method: 'POST',
        body: params,
      },
    );
    return response;
  };

  archiveCorruptionTemplate = async (params: ArchiveCorruptionTemplateRequest) => {
    const response = await apiFetch<ArchiveCorruptionTemplateResponse>(
      null,
      'internal/mist-internal/benchmark/corruptions/archiveCorruptionTemplate',
      {
        method: 'POST',
        body: params,
      },
    );
    return response;
  };

  getCorruptionTemplates = async (corruptionTemplateId: string) => {
    const response = await apiFetch<GetCorruptionTemplatesResponse>(
      null,
      `internal/mist-internal/benchmark/corruptions/getCorruptionTemplates?corruptionTemplateId=${corruptionTemplateId}`,
      {
        method: 'GET',
      },
    );
    return response;
  };

  listCorruptionTemplates = async () => {
    const response = await apiFetch<ListCorruptionTemplatesResponse>(
      null,
      'internal/mist-internal/benchmark/corruptions/listCorruptionTemplates',
      {
        method: 'POST',
        body: {},
      },
    );
    return response;
  };

  testRunCorruptionTemplate = async (params: TestRunCorruptionTemplateRequest) => {
    const response = await apiFetch<TestRunCorruptionTemplateResponse>(
      null,
      'internal/mist-internal/benchmark/corruptions/testRunCorruptionTemplate',
      {
        method: 'POST',
        body: params,
      },
    );
    return response;
  };

  getCorruptionSystemPrompt = async () => {
    const response = await apiFetch(
      null,
      'internal/mist-internal/benchmark/corruptions/corruptionSystemPromptConfig',
    );
    return response;
  };

  updateCorruptionSystemPrompt = async (params: {
    value: {
      useSystemPrompt: boolean;
      systemPrompt: string;
    };
  }) => {
    const response = await apiFetch(
      null,
      'internal/mist-internal/benchmark/corruptions/corruptionSystemPromptConfig',
      {
        method: 'POST',
        body: params,
      },
    );
    return response;
  };

  validateUploadTasks = async (params: ValidateTaskUploadRequest) => {
    const response = await apiFetch<ValidateTaskUploadResponse>(
      null,
      'internal/mist-internal/task/validate-task-upload',
      {
        method: 'POST',
        body: params,
      },
    );

    return response;
  };

  getQualitativeFeedbackItemsForEvalRun = async (evalRunId: string) => {
    const response = await apiFetch<FetchFeedbackForRunResponseDTO>(
      null,
      `internal/genai/qualitativeFeedback/run/${evalRunId}?showArchived=true`,
      {
        method: 'POST',
      },
    );
    return response;
  };

  getQualitativeSurveyItemsForEvalRun = async (evalRunId: string) => {
    const response = await apiFetch<ModelQualitativeFeedbackSurveyDTO[]>(
      null,
      `internal/genai/qualitativeFeedback/run/${evalRunId}/surveys?showArchived=true`,
      {
        method: 'GET',
      },
    );
    return response;
  };

  importAutoReviewerConfiguration = async (params: {
    sourceProjectId: string;
    targetProjectId: string;
    llmCheckKeys: string[];
  }) => {
    const response = await apiFetch<any>(
      null,
      `internal/mist-internal/autoReviewer/importConfiguration`,
      {
        method: 'POST',
        body: params,
      },
    );
    return response;
  };
}

export const communicationService = new CommunicationService();
